/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/threads.scss';

import React, {MouseEventHandler} from 'react';
import ThreadMonitor from "../../../models/threadMonitorModel";
import {AwesomeIcon, Loader} from '@dvrd/dvr-controls';
import {ThreadStatus} from "../../../util/interfaces";
import classNames from 'classnames';
import {BolmateButton} from "../../controls";
import * as process from "node:process";

interface Props {
    onStopThread: (thread: ThreadMonitor) => MouseEventHandler;
    onFinishThread: (thread: ThreadMonitor) => MouseEventHandler;
    onCopyId: (id: string) => MouseEventHandler;
    onChangePage: (page: number) => MouseEventHandler;
    onClose: MouseEventHandler;
    threads: ThreadMonitor[];
    page: number;
    loading: boolean;
    lastUpdated: string;
    activeThreads: number;
    total: number;
    limit: number;
}

export default function Threads(props: Props) {
    const {
        loading, lastUpdated, activeThreads, threads, onStopThread, onFinishThread, onCopyId, onChangePage, page,
        total, onClose, limit
    } = props;

    function renderHeader() {
        return (
            <div className='header-container'>
                <BolmateButton onClick={onClose} label='Terug' disabled={loading}/>
                <div className='header-stats'>
                    <label>Actieve threads: {activeThreads}</label>
                    <label className='last-update'>Laatst bijgewerkt: {lastUpdated}</label>
                </div>
            </div>
        )
    }

    function renderBody() {
        return (
            <div className='body-container'>
                <Loader active={loading}/>
                <div className='threads-list'>
                    <div className='row head'>
                        <label>PROCESS ID</label>
                        <label>TYPE</label>
                        <label>STATUS</label>
                        <label>STATUS GEWIJZIGD</label>
                        <label>ACCOUNT</label>
                        <div/>
                    </div>
                    {threads.map(renderThread)}
                    {!threads.length && <label className='empty-message'>Er zijn geen threads gevonden</label>}
                </div>
            </div>
        )
    }

    function renderThread(thread: ThreadMonitor) {
        const {id, translatedType, icon, account, statusChangedAtMoment, status} = thread;
        const iconPrefix = icon === 'mailchimp' ? 'fab' : undefined;
        const changedAt = statusChangedAtMoment.format('DD-MM-YYYY HH:mm');
        const accountId = account ? account.id.substring(0, account.id.indexOf('-')) : null;
        const fullAccountId = account?.id;
        return (
            <div className='row' key={id}>
                <label title={id} onClick={onCopyId(thread.id)} onDoubleClick={onCopyId(thread.processID)}
                       className='click'>{thread.processID}</label>
                <label title={translatedType}><AwesomeIcon name={icon} prefix={iconPrefix}
                                                           className={classNames('icon', status === ThreadStatus.RUNNING && 'active')}/> {translatedType}
                </label>
                {renderStatus(thread)}
                <label title={changedAt}>{changedAt}</label>
                <label className={classNames(fullAccountId && 'click')}
                       onClick={fullAccountId ? onCopyId(fullAccountId) : undefined}>
                    {account ? `${account.companyName} (${accountId})` : 'n.v.t.'}</label>
                <div className='thread-actions'>
                    {status !== ThreadStatus.FINISHED &&
                        <AwesomeIcon name='check-circle' onClick={onFinishThread(thread)}
                                     className='thread-action finish' title='Beëindigen'/>}
                    {status === ThreadStatus.RUNNING &&
                        <AwesomeIcon name='stop-circle' onClick={onStopThread(thread)}
                                     className='thread-action stop' title='Stoppen'/>}
                </div>
            </div>
        )
    }

    function renderStatus(thread: ThreadMonitor) {
        const {status, translatedStatus} = thread;
        const error = thread.getFlex('error');
        return (
            <div className={classNames('status-container', status)}>
                <label className='status'>{translatedStatus}</label>
                {!!error && <div className='error-info-container'>
                    <AwesomeIcon name='info-circle'/>
                    <p className='error-info'>{error}</p>
                </div>}
            </div>
        )
    }

    function renderFooter() {
        const maxPage = Math.ceil(total / limit) - 1;
        const prevDisabled = page === 0;
        const nextDisabled = page === maxPage;
        const from = Math.min(Math.max(limit * page, 1), total);
        const to = Math.min(limit * (page + 1), total);
        return (
            <div className='footer-container'>
                <label className='page-count'>{from} t/m {to} van {total}</label>
                <div className='pagers'>
                    <AwesomeIcon name='chevron-left' className={classNames('pager', prevDisabled && 'disabled')}
                                 onClick={onChangePage(page - 1)}/>
                    <AwesomeIcon name='chevron-right' className={classNames('pager', nextDisabled && 'disabled')}
                                 onClick={onChangePage(page + 1)}/>
                </div>
            </div>
        )
    }

    return (
        <div className='threads-container'>
            {renderHeader()}
            {renderBody()}
            {renderFooter()}
        </div>
    )
}